import React from "react";
import ReactDOM from "react-dom/client";
import "@fontsource/fusion-pixel-12px-proportional-sc/400.css";
import { AppProvider, useApp } from "./provider";
import Inventory from "./components/screens/Inventory";
import Chest from "./components/screens/Chest";
import Pickup from "./components/InventorySlots/containers/Pickup";
import Tooltip from "./components/Tooltip";
import ItemInspector from "./components/ItemInspector";
import { INVENTORIES } from "./constants";
import "./index.css";

/**
 * Inner React component — full vanilla Minecraft inventory UI.
 * Receives data from the host via "set-inventory" custom events.
 */
function InventoryApp({ readonly, host }) {
  const { setInventory, setSkin, inspectedItem, setInspectedItem, items, itemNames } = useApp();
  const view = host.getAttribute("view");
  const bgImg = "/mc-inventory-static/inventory.png";

  React.useEffect(() => {
    const handler = (e) => {
      const { type, items } = e.detail;
      if (!type || !items) return;
      const cfg = INVENTORIES[type];
      if (!cfg) return;
      const totalSlots = cfg.rows * cfg.cols;
      const padded = [...items];
      while (padded.length < totalSlots) {
        padded.push({ id: null, count: 0 });
      }
      setInventory(type, padded.slice(0, totalSlots));
    };

    const skinHandler = (e) => {
      if (e.detail?.url) setSkin(e.detail.url);
    };

    // 事件只在当前 Web Component 宿主内传递，避免页面同时渲染实时和历史
    // 背包时互相覆盖状态。
    host.addEventListener("set-inventory", handler);
    host.addEventListener("set-skin", skinHandler);
    return () => {
      host.removeEventListener("set-inventory", handler);
      host.removeEventListener("set-skin", skinHandler);
    };
  }, [host, setInventory, setSkin]);

  return (
    <>
      {view === "chest"
        ? <Chest includePlayerInventory={false} title="末影箱" bgImg="/mc-inventory-static/chest.png" />
        : <Inventory bgImg={bgImg} />}
      <Pickup />
      <Tooltip />
      {readonly && inspectedItem && (
        <ItemInspector
          item={inspectedItem}
          items={items}
          itemNames={itemNames}
          onClose={() => setInspectedItem(null)}
        />
      )}
    </>
  );
}

/**
 * Web Component: <mc-inventory>
 *
 * Usage in Vue:
 *   const el = document.getElementById('mc-inventory');
 *   el.setInventory('armor',    [{ id: 311, count: 1 }, ...]);   // 4 slots
 *   el.setInventory('offhand',  [{ id: 1053, count: 1 }]);       // 1 slot
 *   el.setInventory('hotbar',   [{ id: 797, count: 1 }, ...]);   // 9 slots
 *   el.setInventory('inventory',[{ id: 23, count: 64 }, ...]);   // 27 slots
 *   el.setSkin('/api/player-skin/username');                     // 3D model skin URL
 */
class McInventory extends HTMLElement {
  static get observedAttributes() {
    return ["skin"];
  }

  constructor() {
    super();
    this._reactRoot = null;
    this._mounted = false;
  }

  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;
    this._readonly = this.hasAttribute("readonly");
    this._reactRoot = ReactDOM.createRoot(this);
    this._reactRoot.render(
      <React.StrictMode>
          <AppProvider
            readonly={this._readonly}
            useDefaultInventory={this.getAttribute("view") !== "chest"}
          >
          <InventoryApp readonly={this._readonly} host={this} />
        </AppProvider>
      </React.StrictMode>
    );

    if (this.hasAttribute("skin")) {
      const url = this.getAttribute("skin");
      if (url) {
        setTimeout(() => this.setSkin(url), 50);
      }
    }
  }

  disconnectedCallback() {
    if (this._reactRoot) {
      this._reactRoot.unmount();
      this._reactRoot = null;
      this._mounted = false;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "skin" && oldValue !== newValue && newValue) {
      this.setSkin(newValue);
    }
  }

  setInventory(type, items) {
    this.dispatchEvent(
      new CustomEvent("set-inventory", {
        detail: { type, items },
        bubbles: true,
      })
    );
  }

  setSkin(url) {
    this.dispatchEvent(
      new CustomEvent("set-skin", {
        detail: { url },
        bubbles: true,
      })
    );
  }
}

if (!customElements.get("mc-inventory")) {
  customElements.define("mc-inventory", McInventory);
}

export default McInventory;
